import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { MonthYearPicker } from "../components/Filters";
import { MONTHS, formatRupiah } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Receipt, Loader2 } from "lucide-react";

const STATUS = { belum_diterima: { t: "Belum Diterima", c: "bg-rose-100 text-rose-700" }, sebagian: { t: "Diterima Sebagian", c: "bg-amber-100 text-amber-700" }, lunas: { t: "Lunas", c: "bg-emerald-100 text-emerald-700" } };

export default function Income() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [schools, setSchools] = useState([]);
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ school_id: "", label: "", date_received: "", amount: "", method: "", note: "", status: "lunas" });

  useEffect(() => { api.get("/schools").then((r) => setSchools(r.data)); }, []);
  const load = () => api.get(`/school-income?year=${year}&month=${month}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [year, month]);

  const sName = (id) => schools.find((s) => s.id === id)?.name;

  const openNew = () => {
    setEditingId(null);
    setForm({ school_id: "", label: "", date_received: "", amount: "", method: "", note: "", status: "lunas" });
    setDialog(true);
  };

  const openEdit = (i) => {
    setEditingId(i.id);
    setForm({
      school_id: i.school_id || "", label: i.label || "", date_received: i.date_received || "",
      amount: String(i.amount), method: i.method || "", note: i.note || "", status: i.status || "lunas",
    });
    setDialog(true);
  };

  const save = async () => {
    const amt = parseInt(form.amount, 10);
    if (isNaN(amt) || amt < 0) { toast.error("Nominal tidak valid"); return; }
    if (!form.school_id && !form.label) { toast.error("Pilih sekolah atau isi keterangan lain-lain"); return; }
    const body = {
      school_id: form.school_id || null, label: form.school_id ? null : form.label,
      year, month, date_received: form.date_received || null, amount: amt,
      method: form.method || null, note: form.note || null, status: form.status,
    };
    try {
      if (editingId) await api.put(`/school-income/${editingId}`, body);
      else await api.post("/school-income", body);
      toast.success(editingId ? "Penerimaan diperbarui" : "Penerimaan disimpan");
      setDialog(false); setEditingId(null);
      setForm({ school_id: "", label: "", date_received: "", amount: "", method: "", note: "", status: "lunas" }); load();
    } catch (e) { toast.error("Gagal"); }
  };

  const del = async (id) => { try { await api.delete(`/school-income/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error("Gagal"); } };

  return (
    <div className="space-y-5" data-testid="income-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Penerimaan Sekolah</h2>
          <p className="text-sm text-muted-foreground">{MONTHS[month - 1]} {year}</p>
        </div>
        <div className="flex gap-2">
          <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
          <Button onClick={openNew} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-income-btn"><Plus className="w-4 h-4" /> Tambah</Button>
        </div>
      </div>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 bg-white" data-testid="income-total-card">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center"><Receipt className="w-6 h-6 text-white" /></div>
          <div>
            <p className="text-xs text-muted-foreground">Total Penerimaan GTS</p>
            <p className="font-nunito font-extrabold text-2xl text-gtsnavy">{data ? formatRupiah(data.total) : "-"}</p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
        <div className="overflow-x-auto gts-scroll">
          <table className="w-full text-sm">
            <thead><tr className="bg-gtsnavy text-white">
              <th className="text-left px-4 py-3">Sumber</th><th className="text-left px-4 py-3">Tanggal</th>
              <th className="text-right px-4 py-3">Nominal</th><th className="text-left px-4 py-3">Metode</th>
              <th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {(!data || data.items.length === 0) && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Belum ada penerimaan.</td></tr>}
              {data?.items.map((i, idx) => (
                <tr key={i.id} className={idx % 2 ? "bg-orange-50/30" : ""} data-testid={`income-row-${i.id}`}>
                  <td className="px-4 py-2.5 font-medium text-gtsnavy">{i.school_id ? sName(i.school_id) : (i.label || "Lain-lain")}</td>
                  <td className="px-4 py-2.5">{i.date_received || "-"}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{formatRupiah(i.amount)}</td>
                  <td className="px-4 py-2.5">{i.method || "-"}</td>
                  <td className="px-4 py-2.5"><Badge className={`rounded-full ${STATUS[i.status]?.c}`}>{STATUS[i.status]?.t}</Badge></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(i)} className="text-gtsnavy/60 hover:text-gtsnavy mr-3" data-testid={`edit-income-${i.id}`}><Pencil className="w-4 h-4 inline" /></button>
                    <button onClick={() => del(i.id)} className="text-rose-400 hover:text-rose-600" data-testid={`del-income-${i.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Edit Penerimaan" : "Tambah Penerimaan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Sekolah</Label>
              <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v === "lain" ? "" : v, label: v === "lain" ? form.label : "" })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="income-school-select"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
                <SelectContent>
                  {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  <SelectItem value="lain">Lain-lain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!form.school_id && <div><Label>Keterangan Lain-lain</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-xl mt-1" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" data-testid="income-amount" /></div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Metode</Label><Input value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="rounded-xl mt-1" placeholder="Transfer / Tunai" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lunas">Lunas</SelectItem>
                    <SelectItem value="sebagian">Diterima Sebagian</SelectItem>
                    <SelectItem value="belum_diterima">Belum Diterima</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Keterangan</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-income-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
